import React, {useState} from 'react';
import styled from 'styled-components';
import {TscCheckboxAlignment, InlineLabeledCheckbox, TscButtonType, ControlButton, Glyphs, InlineFieldGroup} from '@techsmith/tsc-cloud-style-guide';
import config from '../../../service/config';
import mediaApi from '../../../service/mediaApi';
import {useTranslation} from 'react-i18next';
import {SetupWorkerState} from '../../../model/workerState';
import {StyledWarning} from '../../util/StyledElements';
import withMemoizedContexts from '../../../context/contextContainerHoC';
import {useTscTheme} from '../../../context/themeProvider';
import {mediaStore, updateMediaState, updateMediaStateFromServer} from '../../../context/mediaProvider';
import accessPhraseApi from '../../../service/accessPhraseApi';
import ShareModal from '../../modals/ShareModal';
import {MediaPrivacy} from '../../../model/mediaDetails';
import {AlertModalPortal} from '../../util/ModalPortalHelper';
import TabContentWrapper from '../TabContentWrapper';
import {IBaseLibrary} from '../../../model/library';

const Container = styled.div`
   flex-shrink: 0;
   display: flex;
   flex-direction: column;
   margin-left: 16px;
   label {
      font-size: 0.875rem;
      font-weight: 600;
   }
`;

const Failure = styled(StyledWarning)`
   margin: 0;
   max-width: fit-content;
`;

const Spacer = styled.div`
   > :not(:first-child) {
      margin-top: .5rem;
   }
`;

export const MediaSettingsBase: React.FC<IStateMappedProps> = ({HasAccessPhrase, IsYouTube, IsVideo, DownloadEnabled, Hash, AllowViewerToFastForwardVideo, OwnerLibrary}) => {
   const {t} = useTranslation();
   const theme = useTscTheme();
   const [workerState, setWorkerState] = SetupWorkerState();
   const [showAlert, setShowAlert] = React.useState(false);
   const [showShareModal, setShowShareModal] = useState(false);
   const showDownloadMedia = config.watchConfig.viewersCanDownloadMedia && !IsYouTube;
   const showSmartPlayerConfig = IsVideo && !IsYouTube;
   const showAccessPhrase = config.featureSwitches.EnableMediaAccessPhrase && config.watchConfig.media.Privacy !== MediaPrivacy.private;

   const onWarningConfirm = async () => {
      setShowAlert(false);
      setWorkerState({isWorking: true, hasError: false});

      const downloadEnabled = !DownloadEnabled;

      try {
         await mediaApi.updateDownloadEnabled(Hash, downloadEnabled);
         updateMediaState({DownloadEnabled: downloadEnabled});
         setWorkerState({isWorking: false, hasError: false});
      } catch {
         setWorkerState({isWorking: false, hasError: true});
      }
   };

   const onToggleDownload = async () => {
      if (!DownloadEnabled) {
         setShowAlert(true);
      } else {
         await onWarningConfirm();
      }
   };

   const onToggleAllowViewerToFastForwardVideo = async () => {
      const allowViewerToFastForwardVideo = !AllowViewerToFastForwardVideo;
      setWorkerState({isWorking: true, hasError: false});
      try {
         await mediaApi.updateAllowViewerToFastForwardVideo(Hash, allowViewerToFastForwardVideo);
         updateMediaState({AllowViewerToFastForwardVideo: allowViewerToFastForwardVideo});
         setWorkerState({isWorking: false, hasError: false});
      } catch {
         setWorkerState({isWorking: false, hasError: true});
      }
   };

   const onToggleAccessPhrase = async () => {
      updateMediaState({HasAccessPhrase: !HasAccessPhrase});

      // on turning off access phrase
      if (HasAccessPhrase) {
         await mediaApi.clearMediaAccessPhrase(Hash);
      } else {
         // on turning on access phrase
         await mediaApi.updateMediaAccessPhrase(Hash, await accessPhraseApi.generateAccessPhrase());
      }

      updateMediaStateFromServer();
   };

   const onWarningClose = () => {
      setShowAlert(false);
   };

   const showMediaSettings = () => showDownloadMedia || showSmartPlayerConfig || showAccessPhrase;

   const AccessPhraseSettings = (
      <InlineFieldGroup>
         <InlineLabeledCheckbox
            testId="enable-accessphrase-test-id"
            isChecked={HasAccessPhrase}
            isCentered={false}
            onClick={onToggleAccessPhrase}
            checkboxAlignment={TscCheckboxAlignment.left}
            labelText={t('accessPhrase.protected')}
            disabled={workerState.isWorking}
         />
         <ControlButton
            buttonType={TscButtonType.secondary}
            glyph={<Glyphs.Settings16x16 />}
            themeName={theme}
            onClick={() => setShowShareModal(true)}
            title={t('accessPhrase.manage')}
            testId="accessphrase-copy-button"
         />
      </InlineFieldGroup>
   );

   const warningModal =
      (
         <AlertModalPortal
            modalWidth="28rem"
            dismissButtonType={TscButtonType.secondary}
            dismissContent={t('general.cancel')}
            confirmButtonType={TscButtonType.primary}
            closeContent={t('general.cancel')}
            confirmContent={t('privacy.settingsMenu.continue')}
            onDismiss={onWarningClose}
            onClose={onWarningClose}
            onConfirm={onWarningConfirm}
            title={t('privacy.settingsMenu.warningTitle')}
         >
            {t('privacy.settingsMenu.warningText')}
         </AlertModalPortal>
      );

   return !showMediaSettings() ? null : (
      <TabContentWrapper
         showSpinner={workerState.isWorking}
         showError={workerState.hasError}
      >
         <Container data-test-id="privacy-settings-container">
            {showAlert && warningModal}
            <Spacer>
               {showDownloadMedia && (
                  <InlineLabeledCheckbox
                     isChecked={DownloadEnabled}
                     isCentered={false}
                     onClick={onToggleDownload}
                     checkboxAlignment={TscCheckboxAlignment.left}
                     labelText={t('privacy.settingsMenu.download')}
                     disabled={workerState.isWorking}
                     testId="downLoadMediaOption"
                  />
               )}
               {showSmartPlayerConfig && (
                  <InlineLabeledCheckbox
                     testId="AllowViewerToFastForwardVideoInlineCheckbox"
                     isChecked={AllowViewerToFastForwardVideo}
                     isCentered={false}
                     onClick={onToggleAllowViewerToFastForwardVideo}
                     checkboxAlignment={TscCheckboxAlignment.left}
                     labelText={t('privacy.settingsMenu.allowViewerToFastForwardVideo')}
                     disabled={workerState.isWorking}
                  />
               )}
               {showAccessPhrase && AccessPhraseSettings}
               {workerState.hasError && (
                  <Failure>
                     {t('privacy.settingsMenu.failure')}
                  </Failure>
               )}
            </Spacer>
            {showShareModal && (
               <ShareModal
                  mediaLibraryType={OwnerLibrary.LibraryType}
                  onClose={() => {
                     setShowShareModal(false);
                  }}
               />
            )}
         </Container>
      </TabContentWrapper>
   );
};

export interface IStateMappedProps {
   IsYouTube: boolean;
   IsVideo: boolean;
   DownloadEnabled: boolean;
   Hash: string;
   AllowViewerToFastForwardVideo: boolean;
   HasAccessPhrase: boolean;
   OwnerLibrary: IBaseLibrary;
}

const mapStatesToProps = (
   // eslint-disable-next-line no-empty-pattern
   {},
   {IsYouTube, IsVideo, DownloadEnabled, Hash, AllowViewerToFastForwardVideo, HasAccessPhrase, OwnerLibrary}: Partial<IStateMappedProps>
): IStateMappedProps => ({IsYouTube, IsVideo, DownloadEnabled, Hash, AllowViewerToFastForwardVideo, HasAccessPhrase, OwnerLibrary});

export default withMemoizedContexts(mapStatesToProps, mediaStore)(MediaSettingsBase);
